import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api';

export default function ContractSign() {
  const token = window.location.pathname.replace('/contract/', '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signerName, setSignerName] = useState('');
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const canvasRef = useRef();
  const lastPos = useRef(null);

  useEffect(() => {
    api.getPublicContract(token)
      .then(r => {
        if (r.error) { setError(r.error); return; }
        setData(r.contract);
        if (r.contract?.signed_at) setSigned(true);
      })
      .catch(() => setError('Lien invalide ou expiré'))
      .finally(() => setLoading(false));
  }, [token]);

  // Canvas drawing
  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches?.[0] || e;
    return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    lastPos.current = pos;
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    const pos = getPos(e, canvas);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
    setHasSignature(true);
  };

  const endDraw = (e) => {
    e?.preventDefault();
    setIsDrawing(false);
    lastPos.current = null;
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handleSign = async () => {
    if (!signerName.trim()) { alert('Merci de saisir votre nom complet'); return; }
    if (!hasSignature) { alert('Merci de signer dans le cadre prévu'); return; }
    const signatureData = canvasRef.current.toDataURL('image/png');
    setSigning(true);
    try {
      const r = await api.signContract(token, signatureData, signerName);
      if (r.error) throw new Error(r.error);
      setSigned(true);
    } catch (e) {
      alert(e.message || 'Erreur lors de la signature');
    } finally {
      setSigning(false);
    }
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
      ⏳ Chargement...
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'white', borderRadius: '1rem', padding: '2rem', textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', maxWidth: '400px' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔒</div>
        <h2 style={{ color: '#1e293b', marginBottom: '0.5rem' }}>Lien invalide</h2>
        <p style={{ color: '#64748b' }}>{error}</p>
      </div>
    </div>
  );

  const c = data;

  // Already signed
  if (signed && c?.signed_at) return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'white', borderRadius: '1rem', padding: '2rem', textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', maxWidth: '450px' }}>
        <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>✅</div>
        <h2 style={{ color: '#16a34a', marginBottom: '0.5rem' }}>Document signé</h2>
        <p style={{ color: '#64748b', marginBottom: '1rem' }}>Ce document a été signé électroniquement.</p>
        <div style={{ background: '#f0fdf4', borderRadius: '0.5rem', padding: '1rem', fontSize: '0.85rem', color: '#166534' }}>
          <div>Signataire : <strong>{c.signer_name}</strong></div>
          <div>Date : <strong>{new Date(c.signed_at).toLocaleString('fr-FR')}</strong></div>
        </div>
      </div>
    </div>
  );

  // Post-sign confirmation
  if (signed) return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'white', borderRadius: '1rem', padding: '2rem 2.5rem', textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', maxWidth: '480px' }}>
        <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🎉</div>
        <h2 style={{ color: '#16a34a', marginBottom: '0.5rem', fontSize: '1.5rem' }}>Document signé avec succès !</h2>
        <p style={{ color: '#64748b', marginBottom: '1rem' }}>
          Votre signature a été enregistrée. Une copie sera transmise à <strong>{c.artisan_name || c.nom_entreprise}</strong>.
        </p>
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '0.5rem', padding: '1rem', fontSize: '0.9rem', color: '#166534' }}>
          <div>Signataire : <strong>{signerName}</strong></div>
          <div>Date : <strong>{new Date().toLocaleString('fr-FR')}</strong></div>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', fontFamily: "'Inter', system-ui, sans-serif" }}>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />

      {/* Header artisan */}
      <div style={{ background: 'white', borderBottom: '1px solid #e2e8f0', padding: '1rem 2rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <div style={{ width: '40px', height: '40px', borderRadius: '0.5rem', background: 'linear-gradient(135deg,#3b82f6,#2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '700', fontSize: '1rem' }}>
          {(c.nom_entreprise || c.artisan_name || 'A').charAt(0)}
        </div>
        <div>
          <div style={{ fontWeight: '700', color: '#1e293b', fontSize: '0.95rem' }}>{c.nom_entreprise || c.artisan_name}</div>
          {c.artisan_email && <div style={{ color: '#64748b', fontSize: '0.8rem' }}>{c.artisan_email}</div>}
        </div>
      </div>

      <div style={{ maxWidth: '700px', margin: '0 auto', padding: '2rem 1rem' }}>

        {/* Contract header */}
        <div style={{ background: 'white', borderRadius: '1rem', padding: '2rem', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
            <div style={{ fontSize: '2.5rem', flexShrink: 0 }}>📄</div>
            <div style={{ flex: 1 }}>
              <h1 style={{ fontSize: '1.4rem', fontWeight: '700', color: '#1e293b', margin: '0 0 0.5rem' }}>{c.title}</h1>
              {c.description && <p style={{ color: '#64748b', margin: '0 0 0.75rem', lineHeight: 1.6 }}>{c.description}</p>}
              {c.client_name && (
                <div style={{ color: '#475569', fontSize: '0.875rem' }}>
                  👤 À l'attention de : <strong>{c.client_name}</strong>
                </div>
              )}
            </div>
          </div>

          {c.file_url && (
            <div style={{ marginTop: '1.25rem', paddingTop: '1.25rem', borderTop: '1px solid #e2e8f0' }}>
              <a
                href={`https://api.akodigitale.fr${c.file_url}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', background: '#eff6ff', color: '#2563eb', padding: '0.6rem 1.25rem', borderRadius: '0.5rem', textDecoration: 'none', fontWeight: '600', fontSize: '0.875rem' }}
              >
                📎 Consulter le document ({c.file_name})
              </a>
            </div>
          )}
        </div>

        {/* Signature zone */}
        <div style={{ background: 'white', borderRadius: '1rem', padding: '2rem', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1e293b', marginBottom: '0.25rem' }}>
            ✍️ Signature électronique
          </h2>
          <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
            En signant, vous acceptez les termes du document ci-dessus. Cette signature a valeur juridique.
          </p>

          {/* Signer name */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{ display: 'block', color: '#374151', fontWeight: '500', fontSize: '0.875rem', marginBottom: '0.4rem' }}>
              Votre nom complet *
            </label>
            <input
              type="text"
              placeholder="Prénom Nom"
              value={signerName}
              onChange={e => setSignerName(e.target.value)}
              style={{ width: '100%', padding: '0.65rem 0.875rem', border: '1.5px solid #d1d5db', borderRadius: '0.5rem', fontSize: '0.95rem', color: '#111827', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
            />
          </div>

          {/* Signature canvas */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{ display: 'block', color: '#374151', fontWeight: '500', fontSize: '0.875rem', marginBottom: '0.4rem' }}>
              Votre signature *
            </label>
            <div style={{ position: 'relative', border: '1.5px solid #d1d5db', borderRadius: '0.5rem', background: '#fafafa', overflow: 'hidden' }}>
              <canvas
                ref={canvasRef}
                width={620}
                height={160}
                style={{ display: 'block', width: '100%', height: '160px', cursor: 'crosshair', touchAction: 'none' }}
                onMouseDown={startDraw}
                onMouseMove={draw}
                onMouseUp={endDraw}
                onMouseLeave={endDraw}
                onTouchStart={startDraw}
                onTouchMove={draw}
                onTouchEnd={endDraw}
              />
              {!hasSignature && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: '0.875rem', pointerEvents: 'none' }}>
                  Signez ici avec votre souris ou votre doigt
                </div>
              )}
            </div>
            {hasSignature && (
              <button
                onClick={clearSignature}
                style={{ marginTop: '0.4rem', background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '0.8rem', padding: 0 }}
              >
                🗑 Effacer la signature
              </button>
            )}
          </div>

          {/* Legal notice */}
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '0.75rem 1rem', fontSize: '0.78rem', color: '#64748b', marginBottom: '1.5rem', lineHeight: 1.5 }}>
            🔒 Cette signature électronique est conforme à la réglementation eIDAS (règlement UE n° 910/2014).
            L'heure et l'adresse IP sont enregistrées comme preuve d'horodatage.
          </div>

          <button
            onClick={handleSign}
            disabled={signing || !hasSignature || !signerName.trim()}
            style={{
              width: '100%', padding: '0.875rem', borderRadius: '0.5rem', border: 'none',
              background: signing || !hasSignature || !signerName.trim()
                ? '#e2e8f0'
                : 'linear-gradient(135deg,#16a34a,#15803d)',
              color: signing || !hasSignature || !signerName.trim() ? '#94a3b8' : 'white',
              fontWeight: '700', fontSize: '1rem', cursor: signing || !hasSignature || !signerName.trim() ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
            }}
          >
            {signing ? '⏳ Signature en cours...' : '✅ Signer le document'}
          </button>
        </div>
      </div>
    </div>
  );
}
